import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";

import { Badge } from "@/components/ui/badge";
import { IStoreUserRecord } from "@/services/store-access-query";

import { DataTableColumnHeader } from "../shared/data-table-column-header";
import { SituationCell } from "@/components/unlock-request/situation-cell";

import { UserRowActions } from "./row-actions";

interface UseUserColumnsOptions {
  storeCode: number;
}

export const useUserColumns = (
  options: UseUserColumnsOptions,
): ColumnDef<IStoreUserRecord>[] => {
  const { storeCode } = options;

  return useMemo(
    () => [
      {
        accessorKey: "cpf",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="CPF" />
        ),
        cell: ({ row }) => row.original.cpf || "-",
      },
      {
        accessorKey: "userCode",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Código" />
        ),
        cell: ({ row }) => row.original.userCode || "-",
      },
      {
        id: "name",
        accessorKey: "userName",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Nome" />
        ),
        cell: ({ row }) => row.original.userName || "-",
      },
      {
        accessorKey: "classification",
        enableSorting: false,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Classificação" />
        ),
        cell: ({ row }) => {
          const classification = row.original.classification;
          if (classification === "Desconto") {
            return <Badge variant="secondary">Desconto</Badge>;
          }
          if (classification === "Caixa") {
            return <Badge variant="secondary">Caixa</Badge>;
          }
          if (classification === "Tesouraria") {
            return <Badge variant="secondary">Tesouraria</Badge>;
          }
          return <Badge variant="outline">-</Badge>;
        },
      },
      {
        accessorKey: "situation",
        enableSorting: false,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Situação" />
        ),
        cell: ({ row }) => (
          <SituationCell
            situation={row.original.situation}
            situationStatus={row.original.situationStatus}
          />
        ),
      },
      {
        accessorKey: "createdAt",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Data Criação" />
        ),
        cell: ({ row }) => {
          const date = row.original.createdAt;
          if (!date) return "-";
          return new Date(date).toLocaleDateString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
        },
      },
      {
        accessorKey: "blocked",
        enableSorting: false,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Status" />
        ),
        cell: ({ row }) => {
          const blocked = row.original.blocked;
          return (
            <Badge variant={blocked ? "destructive" : "outline"}>
              {blocked ? "Bloqueado" : "Ativo"}
            </Badge>
          );
        },
      },
      {
        id: "actions",
        enableSorting: false,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Ações" />
        ),
        cell: ({ row }) => (
          <UserRowActions record={row.original} storeCode={storeCode} />
        ),
      },
    ],
    [storeCode],
  );
};
